import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../../context/AuthProvider';
import {
  useCreateMessageMutation,
  useCreateThreadMutation,
  useGetThreadQuery,
  useGetThreadsByAssistantQuery,
} from '../../../../generated/graphqlModels';

// The most recently updated thread. A block can have several threads (each
// "new chat" adds one), and the user should land in the latest of them.
// Threads without updatedAt count as oldest.
const newestThread = <T extends { updatedAt?: any | null }>(
  threads: T[],
): T | undefined => {
  const time = (t: T) => (t.updatedAt ? new Date(t.updatedAt).getTime() : 0);
  return threads.reduce<T | undefined>(
    (newest, t) => (!newest || time(t) > time(newest) ? t : newest),
    undefined,
  );
};

const useThread = (
  assistantId?: string,
  blockId?: string,
  preferedThreadId?: string,
  studyGroupId?: string,
) => {
  const user = useContext(AuthContext);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

  const { data: threadsByAssistant, loading: loadingThreadsByAssistant } =
    useGetThreadsByAssistantQuery({
      variables: { assistantId: assistantId!, userId: user!.pk },
      skip: !assistantId || !user,
      fetchPolicy: 'network-only',
    });

  const { data: threadData, loading: loadingThread } = useGetThreadQuery({
    variables: {
      threadId: selectedThreadId!,
    },
    skip: !selectedThreadId,
    fetchPolicy: 'network-only',
  });

  const [createThread, { loading: loadingCreateThread }] =
    useCreateThreadMutation();

  const [createMessage, { loading: isCreatingMessage }] =
    useCreateMessageMutation();

  useEffect(() => {
    if (!assistantId) {
      return;
    }
    if (threadsByAssistant) {
      // If user has explicitly selected a thread (preferedThreadId), prioritize that
      let threadByAssistant;
      if (preferedThreadId) {
        threadByAssistant = threadsByAssistant.threads.find((t) => t.id === preferedThreadId);
      }
      
      // If no preferedThreadId or not found, fall back to blockId logic
      if (!threadByAssistant) {
        threadByAssistant = blockId
          ? threadsByAssistant.threads.filter((t) => t.block === blockId).at(-1)
          //newestThread(
              //threadsByAssistant.threads.filter((t) => t.block === blockId),
            //)
          : threadsByAssistant.threads.filter((t) => !t.block).at(-1);
      }

      if (threadByAssistant) {
        setSelectedThreadId(threadByAssistant.id);
      } else {
        createNewThread().then();
      }
    }
  }, [threadsByAssistant, preferedThreadId, blockId, studyGroupId]);

  const createNewThread = async () => {
    if (!assistantId) {
      return;
    }
    const data = await createThread({
      variables: {
        assistantId,
        ...((blockId && { blockId }) || null),
        ...((studyGroupId && { studyGroupId }) || null),
      },
    });
    const threadId = data.data?.createThread.id;
    if (threadId) {
      setSelectedThreadId(threadId);
    }
  };

  const clearThread = async () => {
    await createNewThread();
  };

  const sendMessage = (content: string) => {
    if (!selectedThreadId) {
      return;
    }
    createMessage({
      variables: {
        content,
        threadId: selectedThreadId,
      },
    });
  };

  const switchThread = (threadId: string) => {
    setSelectedThreadId(threadId);
  };

  return {
    sendMessage,
    clearThread,
    isCreatingMessage,
    thread: threadData?.thread,
    isLoading:
      loadingThreadsByAssistant || loadingCreateThread || loadingThread,
    switchThread,
  };
};

export default useThread;
